import { Button, Tabs } from 'antd';
import { MaterialType } from 'api';
import RunningTotal from 'components/runningtotal/running-total';
import React, { RefObject, useRef, useState } from 'react';
import { useAppState } from 'state/appstate/appstate';
import { useElementHeight } from 'helpers/useElementHeight';
import './style.css';
import TabPane from './tabpane';
import { materialsForStyle } from './materials-for-style';
import { useSmallScreen } from 'helpers/isSmallScreen';
import Typography from 'helpers/fonts';
import { ArrowLeftOutlined, RightOutlined } from '@ant-design/icons';

const MaterialsSidebar: React.FunctionComponent = () => {
  const isSmallScreen = useSmallScreen();
  let ref: RefObject<HTMLDivElement> = useRef(null);
  const runningTotalHeight = useElementHeight(ref);

  const materialTypes = useAppState().styleProduct.product?.materialTypes;
  const [activeType, setActiveType] = useState<MaterialType | undefined>(
    isSmallScreen ? undefined : materialTypes?.[0]
  );

  const { materials, materialFolders } = useAppState((state) => {
    const materialFolders = state.styleProduct.product?.materialFolders;
    // Only the displayed list is narrowed. materialsDbIdToShapeDiverId keeps
    // every material so saved configurations holding a now unavailable one
    // still resolve to a texture instead of rendering an untextured roof.
    const unavailableForStyle = new Set(
      state.styleProduct.style?.unavailableOptionIds ?? []
    );
    const materials = state.styleProduct.product?.materials;
    return {
      materialFolders,
      materials:
        materials &&
        materialsForStyle(materials, unavailableForStyle).filter(
          ({ materialFolderId }) =>
            materialFolders?.find((f) => f.id === materialFolderId)
        ),
    };
  });

  const materialTypeTabChange = (key: string) => {
    const materialType = materialTypes?.find(
      ({ displayName }) => displayName === key
    );
    if (materialType) {
      setActiveType(materialType);
    }
  };

  return (
    <>
      {isSmallScreen ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            background: Theme['@grey-lightest'],
          }}
        >
          {!activeType &&
            materialTypes &&
            materialTypes.map((mType) => (
              <Button
                block
                type="link"
                key={mType.displayName}
                onClick={() => {
                  materialTypeTabChange(mType.displayName);
                }}
                style={{
                  height: 48,
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: Theme['@black'],
                }}
              >
                <Typography style={{ color: Theme['@black'] }} styleid={6}>
                  {mType.displayName}
                </Typography>

                <RightOutlined />
              </Button>
            ))}
          {activeType && (
            <>
              <div
                onClick={() => setActiveType(undefined)}
                style={{
                  display: 'grid',
                  padding: '0 16px',
                  gridTemplateColumns: 'auto 1fr',
                  overflow: 'none',
                }}
              >
                <ArrowLeftOutlined />
                <Typography
                  style={{ color: Theme['@black'], textAlign: 'center' }}
                  styleid={6}
                >
                  {activeType.displayName}
                </Typography>
              </div>
              <TabPane
                materialType={activeType}
                filters={materialFolders?.filter(
                  (folder) => activeType.materialFolderIds?.includes(folder.id) // filter folders based on the type
                )}
                description={activeType.displayName}
                options={materials?.filter(
                  (mat) => mat.materialTypeId === activeType.id
                )}
                runningTotalHeight={0}
              />
            </>
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: Theme['@white'],
          }}
        >
          <Tabs
            tabPosition={'left'}
            style={{ height: '100%', width: '100%' }}
            destroyInactiveTabPane={true}
            onChange={materialTypeTabChange}
          >
            {materialTypes?.map((mType) => {
              return (
                <Tabs.TabPane
                  tab={mType.displayName
                    ?.toUpperCase()
                    ?.split(' ')
                    .map((part) => (
                      <div>{part}</div>
                    ))}
                  key={mType.displayName}
                  style={{ paddingRight: 0, height: '100%', maxWidth: '100%' }}
                >
                  {
                    <TabPane
                      materialType={mType}
                      filters={materialFolders?.filter(
                        (folder) => mType.materialFolderIds?.includes(folder.id) // filter folders based on the type
                      )}
                      description={mType.displayName}
                      options={materials?.filter(
                        (mat) =>
                          mat.materialTypeId === mType.id && mat.isVisible
                      )}
                      runningTotalHeight={runningTotalHeight}
                    />
                  }
                </Tabs.TabPane>
              );
            })}
          </Tabs>
          <div ref={ref}>
            <RunningTotal />
          </div>
        </div>
      )}
    </>
  );
};

export default MaterialsSidebar;
